// ABOUTME: Quick-record dashboard tab showing every child's behavior/action inputs, plus a
// ABOUTME: compact 4-card row per child: week heart calendar, today's timeline, next goal, next chore list

import React from 'react';
import { Avatar, Box, LinearProgress, Paper, Typography } from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import { StarOutlineRounded } from '@mui/icons-material';
import { StormIcon, TriangleIcon } from '../utils/behaviorUtils';
import { EnhancedFamilyDashboard, Child, BehaviorType } from '../types/api';
import { getTodayInFamilyTimezone, formatShortDate } from '../utils/dateUtils';
import { getChildColor, getChildBorderColor } from '../utils/childColorUtils';
import { pickClosestToCompletionGoal } from '../utils/goalUtils';
import BehaviorCountBadge from './BehaviorCountBadge';
import BehaviorTallyButton from './BehaviorTallyButton';
import FlipYourCoinButton from './FlipYourCoinButton';
import ParentResetButton from './Crossing/ParentResetButton';
import OneOnOneTimeButton from './OneOnOneTimeButton';
import MakeItRightButton from './MakeItRightButton';
import AnalyticsTimeChart from './AnalyticsTimeChart';
import HeartCalendar from './HeartCalendar';
import NextResponsibilityCard from './NextResponsibilityCard';

interface BehaviorUIType {
  type: 'star' | 'triangle' | 'square';
  icon: React.ElementType;
  label: string;
  color: string;
}

// Same three behavior inputs used on the per-child dashboard rows
const behaviorTypes: BehaviorUIType[] = [
  { type: 'square', icon: StormIcon, label: 'Consequence', color: 'error.main' },
  { type: 'triangle', icon: TriangleIcon, label: 'Warning', color: 'warning.main' },
  { type: 'star', icon: StarOutlineRounded, label: 'Positive Behavior', color: 'success.main' },
];

// Heart calendar data per child, keyed by childId (issue #1900) — a narrow slice of
// DashboardRedesigned's ChildCalendarState; this component only needs the week-view inputs.
interface ChildHeartState {
  heartDates: string[];
  mendingHeartDates: string[];
  heartLoading: boolean;
}

interface AllKidsQuickRecordTabProps {
  enhancedChildren: EnhancedFamilyDashboard['children'];
  fullChildren: Child[];
  familyId: number;
  timelineRefreshKey: number;
  childHeartState: Record<number, ChildHeartState>;
  onBehaviorClick: (
    childId: number,
    behaviorType: 'star' | 'triangle' | 'square' | BehaviorType
  ) => void | Promise<void>;
  // Shows the parent Reset control, which opens a crossing (Issue #2012)
  crossingEnabled?: boolean;
  onChoreItemToggle: (childId: number, listId: number, itemId: number, isComplete: boolean) => void;
}

/**
 * Renders one compact quick-record row per child: the 6 behavior/action inputs
 * (star / triangle / square + Flip Your Coin / One-on-One Time / Make It Right), or
 * the 4 inputs star / triangle / square / Reset when crossings are enabled,
 * then a row of 4 compact cards — this week's heart calendar, today's timeline,
 * the goal closest to completion, and the first incomplete chore list. Excludes
 * the full month calendar and celebration sections — those live on the per-child tabs.
 */
const AllKidsQuickRecordTab: React.FC<AllKidsQuickRecordTabProps> = ({
  enhancedChildren,
  fullChildren,
  familyId,
  timelineRefreshKey,
  childHeartState,
  onBehaviorClick,
  crossingEnabled = false,
  onChoreItemToggle,
}) => {
  const theme = useTheme();
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
      {enhancedChildren.map((child, index) => {
        const fullChild = fullChildren.find(
          (c) => c.childId === child.childId || c.id === child.childId
        );
        const tallyButtons = behaviorTypes.map((behavior) => {
          const count =
            behavior.type === 'star'
              ? child.todaysBehaviorCounts.starCount
              : behavior.type === 'triangle'
              ? child.todaysBehaviorCounts.triangleCount
              : child.todaysBehaviorCounts.squareCount;

          return (
            <BehaviorTallyButton
              key={behavior.type}
              behaviorType={behavior.type}
              icon={behavior.icon}
              count={count}
              onClick={() => onBehaviorClick(child.childId, behavior.type)}
              size="compact"
              wrapCaption={crossingEnabled}
            />
          );
        });
        return (
          <Paper
            key={child.childId}
            data-testid={`quick-record-child-${child.childId}`}
            elevation={2}
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 1,
              bgcolor: getChildColor(index),
              border: `1px solid ${getChildBorderColor(index)}`, // pairs with this row's own background (issue #1900), not the theme's generic panel-edge border
              py: '24px',
              px: crossingEnabled ? { xs: '16px', sm: '28px' } : '28px',
              mb: '20px',
            }}
          >
            {/* Child name + 6 behavior/action inputs (4 when crossings are enabled).
                alignItems: flex-start (not center) — under nalu-v1 the tally buttons (issue
                #1924) are taller than the action pills (icon+badge+caption vs. just a pill),
                so center-aligning the row floats the tally icons noticeably above the action
                buttons; top-aligning keeps every button's icon at the same height regardless
                of what extends below it. */}
            <Box
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                alignItems: 'flex-start',
                gap: { xs: 1, sm: 1.5 },
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mr: 'auto' }}>
                <Avatar sx={{ width: 52, height: 52, bgcolor: getChildBorderColor(index), color: 'common.white', fontWeight: 700 }}>
                  {child.name.charAt(0)}
                </Avatar>
                <Box>
                  <Typography variant="h6" sx={{ fontSize: '22px', fontWeight: 600, lineHeight: 1 }}>
                    {child.name}
                  </Typography>
                  {fullChild && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontSize: '12.5px' }}>
                      Age {fullChild.age ?? new Date().getFullYear() - fullChild.birthYear}
                    </Typography>
                  )}
                </Box>
              </Box>

              {crossingEnabled ? (
                /* Storm, Triangle, Star, Reset stay together; below sm they take their own line under the name. */
                <Box
                  data-testid="quick-record-buttons"
                  sx={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    flexWrap: 'nowrap',
                    justifyContent: { xs: 'center', sm: 'flex-start' },
                    flexBasis: { xs: '100%', sm: 'auto' },
                    gap: { xs: 1, sm: 1.5 },
                  }}
                >
                  {tallyButtons}
                  <ParentResetButton
                    child={child}
                    count={child.todaysBehaviorCounts.resetCount}
                    onReset={async (childId) => {
                      await onBehaviorClick(Number(childId), BehaviorType.Reset);
                    }}
                  />
                </Box>
              ) : (
                <>
                  {/* Behavior icon buttons */}
                  {tallyButtons}

                  {/* Action buttons — order matches the per-child dashboard: Flip Coin, Make It Right, 1:1 Time */}
                  <BehaviorCountBadge
                    count={child.todaysBehaviorCounts.flipYourCoinCount}
                    color="primary"
                  >
                    <FlipYourCoinButton
                      child={child}
                      onBehaviorRecorded={(behaviorType) =>
                        onBehaviorClick(child.childId, behaviorType)
                      }
                    />
                  </BehaviorCountBadge>
                  <BehaviorCountBadge
                    count={child.todaysBehaviorCounts.makeItRightCount}
                    color="secondary"
                  >
                    <MakeItRightButton
                      child={child}
                      isParentView={true}
                      dailySquareCount={child.todaysBehaviorCounts.squareCount}
                      dailyMakeItRightCount={child.todaysBehaviorCounts.makeItRightCount}
                      onBehaviorRecorded={(behaviorType) =>
                        onBehaviorClick(child.childId, behaviorType)
                      }
                    />
                  </BehaviorCountBadge>
                  <BehaviorCountBadge
                    count={child.todaysBehaviorCounts.oneOnOneTimeCount}
                    color="primary"
                  >
                    <OneOnOneTimeButton
                      child={child}
                      onBehaviorRecorded={(behaviorType) =>
                        onBehaviorClick(child.childId, behaviorType)
                      }
                    />
                  </BehaviorCountBadge>
                </>
              )}
            </Box>

            {/* 4 compact cards: this week's hearts, today's timeline, next goal, next chore list (issue #1900) */}
            {(() => {
              const heartState = childHeartState[child.childId];
              const nextGoal = pickClosestToCompletionGoal(child.activeGoals);
              const goalPct = nextGoal && nextGoal.targetValue > 0
                ? Math.round((nextGoal.currentValue / nextGoal.targetValue) * 100)
                : 0;
              const otherGoalsCount = nextGoal ? child.activeGoals.length - 1 : 0;
              // Cards nested inside a child's row use the theme's lighter background
              // (not a per-child tint), bordered in that child's own color (issue #1900)
              const miniCardSx = {
                borderRadius: '14px', // matches the mockup's inner-card radius exactly — a bare
                // number here would multiply against theme.shape.borderRadius (16 for nalu-v1),
                // producing 32px instead of the intended 14px
                bgcolor: theme.palette.background.default,
                border: `1px solid ${getChildBorderColor(index)}`,
              };
              return (
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', md: 'repeat(4, 1fr)' },
                    gap: 1.5,
                    mt: '20px',
                  }}
                >
                  <Paper elevation={1} sx={{ ...miniCardSx, p: 1.5 }}>
                    <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                      THIS WEEK
                    </Typography>
                    <HeartCalendar
                      heartDates={heartState?.heartDates ?? []}
                      mendingHeartDates={heartState?.mendingHeartDates ?? []}
                      loading={heartState?.heartLoading ?? false}
                      viewMode="week"
                      hideChrome
                      familyTimezone={fullChild?.familyTimezone}
                    />
                  </Paper>

                  {/* Daily timeline — fixed to today, compact so two children fit on a desktop screen.
                      Title hidden because the child's name already appears in the row header above. */}
                  {fullChild && (
                    <Paper elevation={1} sx={{ ...miniCardSx, border: 'none', p: { xs: 1, md: 1.5 } }}>
                      <AnalyticsTimeChart
                        child={fullChild}
                        selectedDate={getTodayInFamilyTimezone(fullChild.familyTimezone)}
                        familyId={familyId}
                        familyTimezone={fullChild.familyTimezone}
                        isAdminContext={false}
                        hideTitle={true}
                        chartHeight={220}
                        refreshKey={timelineRefreshKey}
                        compact
                      />
                    </Paper>
                  )}

                  <Paper elevation={1} sx={{ ...miniCardSx, p: 1.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.75, mb: 1.25 }}>
                      <Typography sx={{ fontSize: '12px', fontWeight: 800, color: 'text.secondary' }}>
                        NEXT GOAL
                      </Typography>
                      <Typography sx={{ fontSize: '10.5px', fontWeight: 700, color: 'text.disabled' }}>
                        closest to the prize
                      </Typography>
                    </Box>
                    {nextGoal ? (
                      <>
                        <Typography sx={{ fontSize: '13px', fontWeight: 700 }}>{nextGoal.goalTitle}</Typography>
                        <Typography sx={{ fontSize: '11.5px', fontStyle: 'italic', color: 'text.secondary', mt: '2px', mb: 1 }}>
                          {nextGoal.rewardText}
                        </Typography>
                        <LinearProgress
                          variant="determinate"
                          value={Math.min(goalPct, 100)}
                          sx={{
                            height: '10px',
                            borderRadius: '5px',
                            bgcolor: alpha(theme.palette.warning.main, 0.25),
                            '& .MuiLinearProgress-bar': { borderRadius: '5px', bgcolor: 'primary.light' },
                          }}
                        />
                        <Typography sx={{ fontSize: '11px', color: 'text.secondary', mt: 0.75 }}>
                          {nextGoal.currentValue}/{nextGoal.targetValue} ({goalPct}%)
                          {nextGoal.startDate ? ` · started ${formatShortDate(nextGoal.startDate)}` : ''}
                        </Typography>
                        {otherGoalsCount > 0 && (
                          <Typography sx={{ fontSize: '11px', fontWeight: 700, color: 'primary.main', mt: 1, cursor: 'pointer' }}>
                            + {otherGoalsCount} more goal{otherGoalsCount === 1 ? '' : 's'} in progress →
                          </Typography>
                        )}
                      </>
                    ) : (
                      <Typography variant="body2" color="text.secondary">No active goals</Typography>
                    )}
                  </Paper>

                  <NextResponsibilityCard
                    choreStatuses={child.choreStatuses}
                    childId={child.childId}
                    onChoreItemToggle={onChoreItemToggle}
                    sx={miniCardSx}
                  />
                </Box>
              );
            })()}
          </Paper>
        );
      })}
    </Box>
  );
};

export default AllKidsQuickRecordTab;
